import { Grid, styled } from "@mui/material";
import { AllowedFileExtensions } from "@rf-l1/file/model/types.model";
import React, { useEffect, useState } from "react";
import { IFileData } from "@rf-l1/file/helpers/create-file-data";
import { RomaProvider, useRoma } from "@rf-l1/roma/RomaContext";
import { RomaExtras } from "@rf-l1/roma/models/Roma.model";
import EmptyFileUploadv2 from "@rf-l1/file/components/EmptyFileUploadv2";
import MxFileViewer from "@rf-l1/file-viewer/MxFileViewer";
import { hexToRgb } from "@mui/system";
import MxTypography from "@rf-l1/MxTypography";
import { IconTrash } from "@tabler/icons-react";
import MxIcons from "@rf-l1/MxIcons";
import { debounce } from "lodash";
import MxUploadStatusIndicator from "@rf-l1/file/MxUploadStatusIndicator";

export interface MxFileViewerFieldV2Props {
  value: IFileData[];
  onChange: (
    value: { target: { name: string; value: IFileData[] } },
    extras?: RomaExtras
  ) => void;
  accept?: AllowedFileExtensions[];
  maxSize?: number;
  minSize?: number;
  direction?: "horizontal" | "vertical";
  itemRender?: (item: IFileData, index: number) => React.ReactNode;
  label?: string;
  height?: string;
  id?: string;
  name?: string;
  uploadMethod?: (data: IFileData) => Promise<void>;
  onBlur?: () => void;
  invalid?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  disabled?: boolean;
  onDeleteFile?: (index: number) => void | Promise<void>;
  onAfterDeleteAllFiles?: (
    current: IFileData[],
    api: { set: (files: IFileData[]) => void }
  ) => void | Promise<void>;
}

const MxFileViewerFieldV2 = (props: MxFileViewerFieldV2Props) => {
  const {
    value,
    onChange,
    accept,
    maxSize,
    minSize,
    direction,
    uploadMethod,
    disabled,
    onDeleteFile,
    label,
    onAfterDeleteAllFiles
  } = props;

  const handleChange = (value: IFileData[], extras?: RomaExtras) => {
    onChange({ target: { name: props.name ?? "", value } }, extras);
  };
  return (
    <RomaProvider
      initialState={value}
      accept={accept}
      maxSize={maxSize}
      minSize={minSize}
      isMultiple={false}
      onDataChange={handleChange}
      direction={direction}
      uploadMethod={uploadMethod}
      disabled={disabled}
      onDeleteFile={onDeleteFile}
      label={label}
      onAfterDeleteAllFiles={onAfterDeleteAllFiles}
    >
      <WrapFileViewer />
    </RomaProvider>
  );
};

const WrapFileViewer = () => {
  const {
    data = [],
    maxSize,
    accept,
    getRootProps,
    getInputProps,
    label
  } = useRoma();
  const file = data[0] || null;

  if (!file) {
    return (
      <div {...getRootProps()}>
        <EmptyFileUploadv2
          label={label ?? ""}
          maxSize={maxSize}
          allowedTypes={accept ?? []}
        />
        <input {...getInputProps()} />
      </div>
    );
  }
  return <LoadFile file={file} />;
};

const LoadFile = ({ file }: { file: IFileData }) => {
  const {
    getRootProps,
    getInputProps,
    disabled,
    uploadMethod,
    onDeleteFile,
    label
  } = useRoma();
  const [stateUpload, setStateUpload] = useState<"uploading" | "uploaded" | "error">(
    file.file ? "uploading" : "uploaded"
  );
  const handleUpload = debounce(async () => {
    if (uploadMethod && file) {
      setStateUpload("uploading");
      await uploadMethod(file)
        .then(() => {
          setStateUpload("uploaded");
        })
        .catch(() => {
          setStateUpload("error");
        });
    }
  }, 200);

  useEffect(() => {
    if (uploadMethod && file && file.file) {
      handleUpload();
    }
    return () => {
      handleUpload.cancel();
    };
  }, [file]);

  return (
    <Grid container {...getRootProps()}>
      <input {...getInputProps()} />
      <Grid container>
        {uploadMethod && (
          <MxUploadStatusIndicator state={stateUpload} position="top-left" />
        )}

        <MxFileViewer url={file.url} type={file.type} height={"225px"} />
        <CustomerFooterContent container>
          <MxTypography
            variant="overline"
            fontWeight={800}
            color={file.type === "application/pdf" ? "error.light" : "primary.main"}
          >
            {file.type === "application/pdf" ? "PDF" : "IMG"}
          </MxTypography>
          <MxTypography variant="caption" fontWeight={800} align="center" lines={1}>
            {label || file.name}
          </MxTypography>
          {!disabled && (
            <MxIcons
              size={20}
              color="error.light"
              onClick={(e: any) => {
                e.stopPropagation();
                onDeleteFile(0);
              }}
            >
              <IconTrash />
            </MxIcons>
          )}
        </CustomerFooterContent>
      </Grid>
    </Grid>
  );
};

const CustomerFooterContent = styled(Grid)(({ theme }) => ({
  padding: "8px 16px",
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  alignItems: "center",
  position: "absolute",
  bottom: 0,
  left: 0,
  zIndex: 2,
  width: "100%",
  borderRadius: "0 0 8px 8px",
  backgroundColor: hexToRgb(theme.palette.grey[100])
}));

export default MxFileViewerFieldV2;
